import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import './BeerBible.less';
import * as api from 'util/api.js';
import { FullPageLoader, StickyHeader } from 'components';
import {
  Button, Empty, Input, InputNumber, Pagination, Select, Space,
} from 'antd';
import { groupBy } from 'lodash';
import { sortByProductNameRelevance } from 'util/productNameSearchRank';
import ProductCard from './ProductCard/ProductCard';
import BloodyMaryScare from './BloodyMaryScare';

const { Search } = Input;

const BLOODY_MARY_PHRASE = 'bloody mary bloody mary bloody mary';

const isBloodyMaryPhrase = (q) => (
  q.trim().toLowerCase().replace(/\s+/g, ' ') === BLOODY_MARY_PHRASE
);

const evaluateIndexRange = (currentPage, productsPerPage) => {
  const start = (currentPage - 1) * productsPerPage;
  const end = currentPage * productsPerPage;
  return [start, end];
};

/** @returns {{ lo: number, hi: number } | null} */
const getProductAbvInterval = (product) => {
  const { abv, abvMin, abvMax } = product;
  if (abv != null && abv !== '') {
    const v = Number(abv);
    if (Number.isFinite(v)) return { lo: v, hi: v };
  }
  const loRaw = abvMin != null && abvMin !== '' ? Number(abvMin) : null;
  const hiRaw = abvMax != null && abvMax !== '' ? Number(abvMax) : null;
  if (loRaw == null && hiRaw == null) return null;
  const lo = loRaw != null && Number.isFinite(loRaw) ? loRaw : Number.NEGATIVE_INFINITY;
  const hi = hiRaw != null && Number.isFinite(hiRaw) ? hiRaw : Number.POSITIVE_INFINITY;
  return { lo, hi };
};

const intervalsOverlap = (aLo, aHi, bLo, bHi) => Math.max(aLo, bLo) <= Math.min(aHi, bHi);

const norm = (v) => String(v ?? '').trim().toUpperCase();

const fieldIncludesQuery = (value, upperQuery) => norm(value).includes(upperQuery);

const applyStructuralFilters = (list, {
  abvMin,
  abvMax,
  releaseYear,
  style,
}) => {
  let out = list;
  const hasAbvFilter = abvMin != null || abvMax != null;
  if (hasAbvFilter) {
    const fLo = abvMin ?? Number.NEGATIVE_INFINITY;
    const fHi = abvMax ?? Number.POSITIVE_INFINITY;
    out = out.filter((p) => {
      const iv = getProductAbvInterval(p);
      if (!iv) return false;
      return intervalsOverlap(iv.lo, iv.hi, fLo, fHi);
    });
  }
  if (releaseYear != null) {
    out = out.filter((p) => (
      p.dateReleased
      && p.dateReleased.isValid?.()
      && p.dateReleased.year() === releaseYear
    ));
  }
  if (style != null && style !== '') {
    out = out.filter((p) => norm(p.style) === norm(style));
  }
  return out;
};

const partitionAndSortTextMatches = (list, query) => {
  const q = query.trim();
  if (!q) return list;
  const upper = q.toUpperCase();
  const nameHits = [];
  const otherHits = [];
  list.forEach((p) => {
    const nameOk = fieldIncludesQuery(p.name, upper);
    const otherOk = ['malts', 'hops', 'artist', 'blurb'].some((key) => (
      fieldIncludesQuery(p[key], upper)
    ));
    if (!nameOk && !otherOk) return;
    if (nameOk) nameHits.push(p);
    else otherHits.push(p);
  });
  const sortedName = sortByProductNameRelevance(nameHits, q);
  const sortedOther = [...otherHits].sort((a, b) => (
    String(a.name ?? '').localeCompare(String(b.name ?? ''))
  ));
  return [...sortedName, ...sortedOther];
};

const BeerBible = () => {
  const [products, setProducts] = useState(null);
  const [allSkus, setAllSkus] = useState([]);

  const [searchInput, setSearchInput] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');

  const [abvFilterMin, setAbvFilterMin] = useState(null);
  const [abvFilterMax, setAbvFilterMax] = useState(null);

  const [selectedReleaseYear, setSelectedReleaseYear] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(10);
  const [scareActive, setScareActive] = useState(false);

  useEffect(() => {
    api.getProducts().then(setProducts);
    api.getAllProductSkus().then((skus) => setAllSkus(skus || []));
  }, []);

  const skusByProduct = useMemo(
    () => groupBy(
      allSkus.filter((s) => !s.isLegacy && !s.deleted),
      (s) => String(s.productMatrixId),
    ),
    [allSkus],
  );

  const getScrollArea = useCallback(
    () => document.getElementById('scrollable-page-area-container'),
    [],
  );

  const scrollToTop = useCallback(() => {
    const el = getScrollArea();
    if (el) el.scrollTop = 0;
  }, [getScrollArea]);

  const releaseYearOptions = useMemo(() => {
    if (!products?.length) return [];
    const years = new Set();
    products.forEach((p) => {
      if (p.dateReleased && p.dateReleased.isValid?.()) {
        years.add(p.dateReleased.year());
      }
    });
    return [...years].sort((a, b) => b - a).map((y) => ({ value: y, label: String(y) }));
  }, [products]);

  const styleOptions = useMemo(() => {
    if (!products?.length) return [];
    const styles = new Set();
    products.forEach((p) => {
      const s = String(p.style ?? '').trim();
      if (s) styles.add(s);
    });
    return [...styles].sort((a, b) => a.localeCompare(b)).map((s) => ({
      value: s,
      label: s,
    }));
  }, [products]);

  const productData = useMemo(() => {
    if (!products) return [];
    let list = applyStructuralFilters(products, {
      abvMin: abvFilterMin,
      abvMax: abvFilterMax,
      releaseYear: selectedReleaseYear,
      style: selectedStyle,
    });
    list = partitionAndSortTextMatches(list, appliedSearchQuery);
    return list;
  }, [
    products,
    abvFilterMin,
    abvFilterMax,
    selectedReleaseYear,
    selectedStyle,
    appliedSearchQuery,
  ]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(productData.length / productsPerPage) || 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [productData.length, productsPerPage, currentPage]);

  const resetPaginationAndScroll = useCallback(() => {
    setCurrentPage(1);
    scrollToTop();
  }, [scrollToTop]);

  const onSearch = (value) => {
    const q = String(value ?? '').trim();
    if (isBloodyMaryPhrase(q)) {
      setScareActive(true);
      setSearchInput('');
      setAppliedSearchQuery('');
      return;
    }
    setAppliedSearchQuery(q);
    resetPaginationAndScroll();
  };

  const onSearchChange = (event) => {
    const v = event.target.value;
    setSearchInput(v);
    if (!v) {
      setAppliedSearchQuery('');
      resetPaginationAndScroll();
    }
  };

  const onPaginationChange = (page, pageSize) => {
    if (page !== currentPage) {
      setCurrentPage(page);
      scrollToTop();
    }
    if (pageSize !== productsPerPage) {
      setProductsPerPage(pageSize);
    }
  };

  const clearAllFilters = () => {
    setSearchInput('');
    setAppliedSearchQuery('');
    setAbvFilterMin(null);
    setAbvFilterMax(null);
    setSelectedReleaseYear(null);
    setSelectedStyle(null);
    resetPaginationAndScroll();
  };

  const onAbvMinChange = (v) => {
    setAbvFilterMin(v);
    resetPaginationAndScroll();
  };

  const onAbvMaxChange = (v) => {
    setAbvFilterMax(v);
    resetPaginationAndScroll();
  };

  const onReleaseYearChange = (v) => {
    setSelectedReleaseYear(v ?? null);
    resetPaginationAndScroll();
  };

  const onStyleChange = (v) => {
    setSelectedStyle(v ?? null);
    resetPaginationAndScroll();
  };

  const hasActiveFilters = Boolean(
    appliedSearchQuery.trim()
    || abvFilterMin != null
    || abvFilterMax != null
    || selectedReleaseYear != null
    || (selectedStyle != null && selectedStyle !== ''),
  );

  return products ? (
    <div id="beer-bible">
      <BloodyMaryScare
        active={scareActive}
        onDone={() => setScareActive(false)}
      />
      <StickyHeader>
        <div className="beer-bible-toolbar">
          <div className="beer-bible-search-wrap">
            <Search
              className="beer-bible-search"
              disabled={!products}
              placeholder="Search name, malts, hops, artist, blurb…"
              value={searchInput}
              onChange={onSearchChange}
              onSearch={onSearch}
              allowClear
            />
          </div>
          <div className="beer-bible-filters">
            <Space wrap size="middle" align="center">
              <span className="beer-bible-filter-label">ABV %</span>
              <InputNumber
                className="beer-bible-abv-input"
                min={0}
                max={100}
                step={0.1}
                placeholder="Min"
                value={abvFilterMin}
                onChange={onAbvMinChange}
              />
              <span className="beer-bible-abv-sep">—</span>
              <InputNumber
                className="beer-bible-abv-input"
                min={0}
                max={100}
                step={0.1}
                placeholder="Max"
                value={abvFilterMax}
                onChange={onAbvMaxChange}
              />
              <Select
                className="beer-bible-select beer-bible-select-year"
                allowClear
                placeholder="Release year"
                options={releaseYearOptions}
                value={selectedReleaseYear ?? undefined}
                onChange={onReleaseYearChange}
              />
              <Select
                className="beer-bible-select beer-bible-select-style"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Style"
                options={styleOptions}
                value={selectedStyle ?? undefined}
                onChange={onStyleChange}
              />
              <Button
                type="default"
                disabled={!hasActiveFilters}
                onClick={clearAllFilters}
              >
                Clear filters
              </Button>
            </Space>
          </div>
        </div>
      </StickyHeader>

      {productData.length > 0 ? (
        <>
          {(productData.slice(
            ...evaluateIndexRange(currentPage, productsPerPage),
          )).map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              skus={skusByProduct[String(product.id)]}
            />
          ))}

          <div id="pagination-container">
            <Pagination
              responsive
              hideOnSinglePage
              current={currentPage}
              pageSize={productsPerPage}
              total={productData.length}
              onChange={onPaginationChange}
              pageSizeOptions={[10, 20, 50]}
            />
          </div>
        </>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}

    </div>
  ) : (<FullPageLoader />);
};

export default BeerBible;
